import { makeObservable, observable, action } from "mobx"
import type { FetchOptions } from "../types"

export type TranslationValues = Record<string, string | number>;

export type OnGetHeadersType = (token?: string, language?: string, headers?: any, listId?: string) => any;
export type OnFetchType = <T>(url: string, options?: FetchOptions, listId?: string) => Promise<T>;
export type OnErrorMessageType = (error: Error) => Promise<string>;
export type OnSerializeType = (params: URLSearchParams, settings: GenericListSettings, listId?: string) => void;
export type OnTranslateType = (key: string, values?: TranslationValues, listId?: string) => string;

/** Responsible for all system settings in use for all generic lists. Allows system specific implementation of generic list components. */
export class GenericListSettings {

  /** Never use directly. Always use getter. */
  private _onFetch: OnFetchType = defaultFetch;
  private _onFetchSet = false;

  /** Never use directly. Always use getter. */
  private _onTranslate: OnTranslateType = defaultTranslate;
  private _onTranslateSet = false;

  constructor() {
    makeObservable(this, {
      ready: observable,
      setReady: action,
      token: observable,
      setToken: action,
      language: observable,
      setLanguage: action,
      params: observable,
      setParams: action
    })
  }

  /** Set after required configuration of onFetch and onTranslate were made. */
  ready: boolean = false;

  /** Internally called after configuration of required settings was done. No need to call it externally. */
  setReady(value: boolean) {
    this.ready = value;
  }

  /** Set to required bearer token for use in authentication of fetch requests. */
  token?: string;
  setToken(value?: string) {
    this.token = value;
  }

  /** Set to required language of user for use in fetch requests to get localized data from server. */
  language?: string;
  setLanguage(value?: string) {
    this.language = value;
  }

  /** Contains current set of values components user for state. */
  params: URLSearchParams = new URLSearchParams();
  /** Set to current set of values for components to use for state. */
  setParams(value: URLSearchParams) {
    this.params = value;
  }

  /** Internally used to serialize params. By default set to internally maintained params. Override for custom onSerialize for custom serialization. */
  serialize(params: URLSearchParams) {
    this.onSerialize(params, this);
  }

  /** Used for fetching data from server. */
  get onFetch() {
    return this._onFetch;
  }
  set onFetch(value: OnFetchType) {
    this._onFetch = value;
    this._onFetchSet = true;
    this.setReady(this._onFetchSet && this._onTranslateSet);
  }

  /** Used for translation of all language sensitive contents. */
  get onTranslate() {
    return this._onTranslate;
  }
  set onTranslate(value: OnTranslateType) {
    this._onTranslate = value;
    this._onTranslateSet = true;
    this.setReady(this._onFetchSet && this._onTranslateSet);
  }

  /** Override to handle custom serialization of user settings like sorting, filtering and paging. */
  onSerialize: OnSerializeType = defaultSerialize;

  /** Override for custom implementation of header generation for fetch requests. */
  onGetHeaders: OnGetHeadersType = defaultOnGetHeaders;

  /** Override for custom implementation of error message extraction after for example fetch requests. */
  onErrorMessage: OnErrorMessageType = defaultErrorMessage;
}

/** Default implementation for making fetch requests to server. Always needs to be overrriden with a custom implementation. */
const defaultFetch: OnFetchType = <T>(url: string, options?: FetchOptions): Promise<T> => {
  return new Promise<T>((resolve, reject) => {
    console.warn('onFetch not configured');
    reject('not implemented');
  });
}

/** Default implementation for handling translations. Always need to be overriden with a custom implementation. */
const defaultTranslate: OnTranslateType = (key: string, options?: TranslationValues) => {
  console.warn('onTranslate not configured');
  return key;
}

/** Default imnplementation of serialization of settings like sorting, filtering and paging made by user */
const defaultSerialize: OnSerializeType = (params: URLSearchParams, settings: GenericListSettings) => {
  settings.setParams(params);
}

/** Default implementation to get headers for requests to server. */
const defaultOnGetHeaders: OnGetHeadersType = (token?: string, language?: string, headers?: any) => {
  const h: any = headers ?? {};

  if (token) {
    h["Authorization"] = "Bearer " + token;
  }

  if (language) {
    h["Accept-Language"] = language;
  }

  return h;
}

/** Default implementation to extract human readible error message from given Error object. */
const defaultErrorMessage: OnErrorMessageType = (error: Error) => {
  return new Promise((resolve) => {
    resolve(error.message)
  });
}